// src/contexts/ChatContext.jsx
import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { io } from "socket.io-client";

export const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [username, setUsername] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loginError, setLoginError] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [socketStatus, setSocketStatus] = useState("connecting");
  const [uploadNotices, setUploadNotices] = useState([]);
  // Credentials live only in memory, to authenticate a replacement socket.
  const credentialsRef = useRef(null);
  const authenticatedRef = useRef(false);
  const loginPendingRef = useRef(false);

  const rawBackend = process.env.REACT_APP_BACKEND_URL || "";
  const backendURL = rawBackend ? rawBackend.replace(/\/api\/?$/, "") : "";

  // Initialize socket connection once on mount
  useEffect(() => {
    if (!backendURL) {
      console.warn("REACT_APP_BACKEND_URL is not configured.");
      setSocketStatus("disconnected");
      return;
    }

    const newSocket = io(backendURL, {
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });
    setSocket(newSocket);

    newSocket.on("connect", () => {
      console.log("Connected to server");
      setIsConnected(true);
      setSocketStatus("connected");
      if (credentialsRef.current) {
        loginPendingRef.current = true;
        newSocket.emit("login", credentialsRef.current);
      }
    });

    newSocket.on("disconnect", (reason) => {
      console.log("Disconnected from server:", reason);
      setIsConnected(false);
      setSocketStatus("disconnected");
      authenticatedRef.current = false;
      setIsAuthenticated(false);
      loginPendingRef.current = false;
      if (reason === "io server disconnect" && credentialsRef.current) {
        newSocket.connect();
      }
    });

    newSocket.on("connect_error", (err) => {
      console.error("Socket connect_error:", err.message);
      setIsConnected(false);
      setSocketStatus("error");
    });

    return () => {
      newSocket.disconnect();
    };
  }, [backendURL]);

  // Set up socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleUpdateUsers = (users) => {
      setOnlineUsers(
        users
          .filter((user) => user !== username)
          .map((user) => ({ value: user, label: user }))
      );
    };

    const handleLoginSuccess = (data) => {
      if (!credentialsRef.current || data.username !== credentialsRef.current.username) return;
      authenticatedRef.current = true;
      setIsAuthenticated(true);
      loginPendingRef.current = false;
      setIsLoggedIn(true);
      setLoginError("");
    };

    const handleLoginError = (data) => {
      // The same event is also used for private-message errors.
      if (!loginPendingRef.current) return;
      credentialsRef.current = null;
      authenticatedRef.current = false;
      setIsAuthenticated(false);
      loginPendingRef.current = false;
      setLoginError(data?.message || "Login failed");
      setIsLoggedIn(false);
      setUploadNotices([]);
      setSelectedRecipient(null);
      console.error("Login error:", data?.message);
    };

    const handleImageUploaded = (data) => {
      if (!authenticatedRef.current || !data ||
          ![data.id, data.filename, data.topic, data.timestamp].every(
            (value) => typeof value === "string" && value.trim()
          ) || !Number.isFinite(Date.parse(data.timestamp))) return;

      const notice = {
        id: data.id,
        filename: data.filename,
        topic: data.topic,
        timestamp: data.timestamp,
      };
      setUploadNotices((previous) => previous.some((item) => item.id === notice.id)
        ? previous
        : [...previous, notice].slice(-100));
    };

    socket.on("update_users", handleUpdateUsers);
    socket.on("login_success", handleLoginSuccess);
    socket.on("login_error", handleLoginError);
    socket.on("error", handleLoginError);
    socket.on("image_uploaded", handleImageUploaded);

    return () => {
      socket.off("update_users", handleUpdateUsers);
      socket.off("login_success", handleLoginSuccess);
      socket.off("login_error", handleLoginError);
      socket.off("error", handleLoginError);
      socket.off("image_uploaded", handleImageUploaded);
    };
  }, [socket, username]);

  const login = useCallback(
    (user, pass) => {
      if (!user || !pass) {
        setLoginError("Username and password are required.");
        return;
      }
      if (!socket) {
        setLoginError("Chat service not ready. Please try again.");
        return;
      }

      setUsername(user);
      setLoginError("");
      credentialsRef.current = { username: user, password: pass };
      authenticatedRef.current = false;
      setIsAuthenticated(false);
      loginPendingRef.current = true;

      if (!socket.connected) {
        socket.connect();
      } else {
        socket.emit("login", credentialsRef.current);
      }
    },
    [socket]
  );

  const logout = useCallback(() => {
    credentialsRef.current = null;
    authenticatedRef.current = false;
    setIsAuthenticated(false);
    loginPendingRef.current = false;
    socket?.disconnect();
    setIsLoggedIn(false);
    setUsername("");
    setOnlineUsers([]);
    setLoginError("");
    setSelectedRecipient(null);
    setUploadNotices([]);
    console.log("Logged out");
  }, [socket]);

  const updateSelectedRecipient = useCallback((recipient) => {
    setSelectedRecipient(recipient);
  }, []);

  const value = {
    socket,
    username,
    isLoggedIn,
    onlineUsers,
    loginError,
    selectedRecipient,
    isConnected,
    isAuthenticated,
    socketStatus,
    uploadNotices,
    login,
    logout,
    updateSelectedRecipient,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
